import React, { useState } from 'react';

import { Box, Button, Divider, Paper, Stack, Typography } from '@mui/material';

import useCurrentUser from '~/hooks/useCurrentUser';

import { canManageInfiniteMember } from '~/utils/luxLoyalty';

import InfiniteLogo from './InfiniteLogo';
import LuxLoyaltyChangeRepDialog from './LuxLoyaltyChangeRepDialog';
import LuxLoyaltyInfiniteOnboardingEmails from './LuxLoyaltyInfiniteOnboardingEmails';
import LuxLoyaltyInfiniteProfileDetails from './LuxLoyaltyInfiniteProfileDetails';

interface Props {
  account: App.LuxLoyaltyAccount;
  customerEmail: string;
  refreshData: () => Promise<void>;
}

function ConciergeSection({ account, refreshData }: Pick<Props, 'account' | 'refreshData'>) {
  const { user } = useCurrentUser();
  const [changeRepDialogOpen, setChangeRepDialogOpen] = useState(false);
  const linkedRep = account.customerServiceRep ?? null;
  const canManageInfinite = canManageInfiniteMember(user);

  return (
    <Box>
      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
        Concierge
      </Typography>
      <Stack spacing={2}>
        <Box>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Concierge representative
          </Typography>
          {linkedRep ? (
            <>
              <Typography variant="body1" fontWeight={600}>
                {linkedRep.firstName} {linkedRep.lastName}
              </Typography>
              <Typography variant="body2" color="text.secondary" mt={0.5}>
                {linkedRep.email} · {linkedRep.phone}
              </Typography>
            </>
          ) : (
            <Typography variant="body1">Not assigned yet.</Typography>
          )}
        </Box>
        <Button
          variant="contained"
          size="medium"
          sx={{ alignSelf: 'flex-start' }}
          onClick={() => setChangeRepDialogOpen(true)}
          disabled={!canManageInfinite}
        >
          {linkedRep ? 'Change rep' : 'Assign rep'}
        </Button>
      </Stack>
      <LuxLoyaltyChangeRepDialog
        open={changeRepDialogOpen}
        onClose={() => setChangeRepDialogOpen(false)}
        customerId={account.customerId}
        currentRepId={linkedRep?.id ?? null}
        onSuccess={() => refreshData()}
      />
    </Box>
  );
}

export default function LuxLoyaltyInfiniteSection({ account, customerEmail, refreshData }: Props) {
  return (
    <Paper variant="outlined" sx={{ p: { xs: 2, md: 2.5 }, borderRadius: 2 }}>
      <Stack spacing={2.5}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          spacing={1.5}
          sx={(theme) => ({
            background: theme.palette.loyaltyTiers.infinite.gradient,
            borderRadius: 1,
            color: theme.palette.loyaltyTiers.infinite.contrastText,
            px: 2,
            py: 1.25,
          })}
        >
          <Stack direction="row" alignItems="center" spacing={1.5} sx={{ minWidth: 0 }}>
            <InfiniteLogo width={150} height={(38 / 248) * 150} aria-label="Infinite" />
            <Typography variant="subtitle2" sx={{ color: 'grey.300', fontWeight: 700 }}>
              Member management
            </Typography>
          </Stack>
        </Stack>
        <ConciergeSection account={account} refreshData={refreshData} />
        <Divider />
        <LuxLoyaltyInfiniteProfileDetails
          profile={account.infiniteProfile}
          onProfileUpdated={refreshData}
          framed={false}
        />
        <Divider />
        <LuxLoyaltyInfiniteOnboardingEmails
          customerId={account.customerId}
          customerEmail={customerEmail}
          hasConciergeRep={!!account.customerServiceRep}
        />
      </Stack>
    </Paper>
  );
}
